import { ReactNode, useState } from 'react';
import { SxProps } from '@mui/system';
import * as S from './HeaderIconMenu.styled';
import { useDebouncedCallback } from '@core-web/hooks';
import Icon from '../Icon';

export enum IconMenuType {
  TRANSITIONAL,
  NORMAL,
  TRANSITIONAL_HIGHLIGHT,
  NORMAL_HIGHLIGHT,
}

export interface IIconMenuProps {
  title?: string;
  highlightContent?: string;
  type: IconMenuType;
  menuIcon: string;
  sx?: SxProps;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  isDisabled?: boolean;
  children?: ReactNode;
  isActive?: boolean;
}

const IconMenu = ({
  type,
  title,
  onClick,
  sx,
  isDisabled,
  menuIcon,
  highlightContent,
  isActive,
}: IIconMenuProps): JSX.Element => {
  const handleOnclick = useDebouncedCallback((e: React.MouseEvent<HTMLElement>) => {
    if (isDisabled) {
      return;
    }
    onClick?.(e);
  }, 300);

  const [isTitleVisible, setIsTitleVisible] = useState(false);
  const isTransitional = type === IconMenuType.TRANSITIONAL || type === IconMenuType.TRANSITIONAL_HIGHLIGHT;
  const handleMouseOver = () => {
    if (!isTransitional) {
      return;
    }
    setIsTitleVisible(true);
  };

  const handleMouseLeave = () => {
    if (!isTransitional) {
      return;
    }
    setIsTitleVisible(false);
  };

  const renderIcon = () => {
    return (
      <S.MenuIcon className='menuIcon'>
        <Icon icon={menuIcon} sx={S.MenuIconStyle} />
        {(type === IconMenuType.NORMAL_HIGHLIGHT || type === IconMenuType.TRANSITIONAL_HIGHLIGHT) &&
          highlightContent && (
            <S.IconBadge onMouseOver={(e) => e.stopPropagation()} onMouseLeave={(e) => e.stopPropagation()}>
              {highlightContent}
            </S.IconBadge>
          )}
      </S.MenuIcon>
    );
  };

  return (
    <S.MenuIconWrapper
      onClick={handleOnclick}
      sx={sx}
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
      className={`${isDisabled ? 'disabled' : ''}${isTitleVisible ? 'visible' : ''}${isActive ? 'active' : ''}`}>
      <S.MenuIconContainer className='menuIconContainer'>
        {renderIcon()}
        <S.MenuIconTitleWrapper className='menuIconTitleWrapper'>
          {type === IconMenuType.TRANSITIONAL && <S.MenuIconTitle>{title}</S.MenuIconTitle>}
          {type === IconMenuType.TRANSITIONAL_HIGHLIGHT && (
            <S.MenuIconTitle>
              {title}
              {highlightContent && <S.IconTitleBadge>{highlightContent}</S.IconTitleBadge>}
            </S.MenuIconTitle>
          )}
        </S.MenuIconTitleWrapper>
      </S.MenuIconContainer>
    </S.MenuIconWrapper>
  );
};

export { IconMenu as default, IconMenu };
